import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useApp } from "@/store/useApp";
import { Logo } from "@/components/Logo";
import {
  LogOut, ShieldCheck, ChevronDown, Menu, X, PanelLeftClose, PanelLeftOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { hasPermission, homeRouteFor, isStaffRole } from "@/lib/types";
import { STAFF_HOME_ROUTE } from "@/lib/staffWelcome";
import { isGroup, buildVisibleNav, type NavEntry } from "./adminNav";
import { TabBar } from "./adminTabs/TabBar";
import { TabKeepAliveHost } from "./adminTabs/TabKeepAliveHost";
import { AdminRoutes } from "./AdminRoutes";
import { useAdminTabs } from "@/store/useAdminTabs";
import { useNexoWelcome } from "@/store/useNexoWelcome";

const PENDING_TOAST_SESSION_KEY = "adminPendingClientsNotified";

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { account, logout } = useApp();
  const clearAllTabs = useAdminTabs((state) => state.clearAll);
  const clearWelcome = useNexoWelcome((state) => state.clear);
  // Logout limpa TODAS as abas internas (requisito #7) e a saudação do Nexo (não vaza nome/estado entre
  // contas quando outro usuário loga sem recarregar a página), além da sessão.
  const handleLogout = async () => {
    await logout();
    clearAllTabs();
    clearWelcome();
    navigate("/login");
  };
  const supplierNfEnabled = useApp((state) => state.supplierNfEnabled);
  const clients = useApp((state) => state.clients);
  const clientsStatus = useApp((state) => state.clientsStatus);
  const bootstrapAdminCustomers = useApp((state) => state.bootstrapAdminCustomers);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("adminSidebarCollapsed") === "1";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("adminSidebarCollapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    if (!account) navigate("/login", { replace: true });
    else if (!isStaffRole(account.role)) navigate("/catalogo", { replace: true });
    else if (account.role === "ENTREGADOR") navigate(homeRouteFor(account), { replace: true });
  }, [account, navigate]);

  useEffect(() => {
    if (!account || !isStaffRole(account.role) || account.role === "ENTREGADOR") return;
    // Consulta auxiliar (badge de pendentes) só para quem pode ver clientes — evita 403 em telas
    // autorizadas de quem não tem CLIENTES.CLIENTE.VISUALIZAR.
    if (!hasPermission(account, "CLIENTES.CLIENTE.VISUALIZAR")) return;
    if (clientsStatus === "idle") void bootstrapAdminCustomers();
  }, [account, clientsStatus, bootstrapAdminCustomers]);

  const pendingClientsCount = useMemo(
    () => clients.filter((client) => client.status === "PENDENTE_VALIDACAO" && client.hasProfile !== false).length,
    [clients],
  );

  useEffect(() => {
    if (clientsStatus !== "ready" || pendingClientsCount === 0) return;
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(PENDING_TOAST_SESSION_KEY) === "1") return;
    window.sessionStorage.setItem(PENDING_TOAST_SESSION_KEY, "1");
    toast.info(
      pendingClientsCount === 1
        ? "1 cliente aguardando validação"
        : `${pendingClientsCount} clientes aguardando validação`,
      {
        description: "Abra Comercial → Clientes para revisar.",
        action: {
          label: "Ver pendentes",
          onClick: () => navigate("/admin/clientes"),
        },
      },
    );
  }, [clientsStatus, pendingClientsCount, navigate]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const permKey = (account?.permissions ?? []).join(",");
  // Navegação visível (ADMIN vê tudo; demais por permissão; itens sob feature flag só com o módulo ligado).
  // Lógica centralizada em adminNav.buildVisibleNav — mesma fonte usada pela página de boas-vindas.
  const visibleNav = useMemo<NavEntry[]>(
    () => buildVisibleNav(account, supplierNfEnabled),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permKey, supplierNfEnabled, account?.role],
  );

  const initialOpen = useMemo(() => {
    const open: Record<string, boolean> = {};
    for (const entry of visibleNav) {
      if (isGroup(entry)) {
        open[entry.id] = entry.items.some((it) => location.pathname.startsWith(it.to));
      }
    }
    return open;
  }, [visibleNav, location.pathname]);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(initialOpen);

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      for (const entry of visibleNav) {
        if (isGroup(entry) && entry.items.some((it) => location.pathname.startsWith(it.to))) {
          next[entry.id] = true;
        }
      }
      return next;
    });
  }, [location.pathname, visibleNav]);

  if (!account || !isStaffRole(account.role) || account.role === "ENTREGADOR") return null;

  const role = account.role;
  const roleLabel = role === "ADMIN" ? "Admin"
    : role === "ADMINISTRATIVO" ? "Administrativo"
    : role === "VENDEDOR" ? "Vendedor"
    : role === "PLANEJADOR" ? "Planejador"
    : role === "COMPRADOR" ? "Comprador"
    : role === "CONFERENTE" ? "Conferente"
    : role === "PRODUCAO" ? "Produção"
    : role === "EXPEDICAO" ? "Expedição"
    : role === "SEPARACAO" ? "Separação IN NATURA"
    : "Staff";

  const toggleGroup = (id: string) =>
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  const renderNav = (variant: "desktop" | "mobile") => (
    <nav className="flex flex-col gap-1 px-3 py-4">
      {visibleNav.map((entry) => {
        if (!isGroup(entry)) {
          const { to, label, icon: Icon, end } = entry;
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  "inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60",
                )
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          );
        }

        const open = openGroups[entry.id] ?? false;
        const GroupIcon = entry.icon;
        const hasActive = entry.items.some((it) => location.pathname.startsWith(it.to));
        const groupBadge =
          !open && entry.items.some((it) => it.to === "/admin/clientes") && pendingClientsCount > 0
            ? pendingClientsCount
            : null;
        return (
          <div key={entry.id} className="flex flex-col">
            <button
              type="button"
              onClick={() => toggleGroup(entry.id)}
              className={cn(
                "inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                hasActive
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/60",
              )}
              aria-expanded={open}
            >
              <GroupIcon className="h-4 w-4" />
              <span className="flex-1 text-left">{entry.label}</span>
              {groupBadge != null && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-semibold text-destructive-foreground">
                  {groupBadge}
                </span>
              )}
              <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
            </button>
            {open && (
              <div className="ml-4 mt-1 flex flex-col gap-1 border-l border-border pl-2">
                {entry.items.map(({ to, label, icon: Icon, end }) => {
                  const badge = to === "/admin/clientes" && pendingClientsCount > 0 ? pendingClientsCount : null;
                  return (
                    <NavLink
                      key={to}
                      to={to}
                      end={end}
                      className={({ isActive }) =>
                        cn(
                          "inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                          isActive
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:text-foreground hover:bg-secondary/60",
                        )
                      }
                    >
                      <Icon className="h-4 w-4" />
                      <span className="flex-1">{label}</span>
                      {badge != null && (
                        <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-semibold text-destructive-foreground">
                          {badge}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {variant === "mobile" && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-4 justify-start"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4" /> Sair
        </Button>
      )}
    </nav>
  );

  // Trilha de ícones (sidebar recolhida no desktop). Itens standalone viram links só-ícone;
  // grupos viram um botão que reabre o menu e expande o próprio grupo.
  const renderCollapsedNav = () => (
    <nav className="flex flex-col items-center gap-1 px-2 py-4">
      {visibleNav.map((entry) => {
        if (!isGroup(entry)) {
          const { to, label, icon: Icon, end } = entry;
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              title={label}
              aria-label={label}
              className={({ isActive }) =>
                cn(
                  "inline-flex h-10 w-10 items-center justify-center rounded-lg transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60",
                )
              }
            >
              <Icon className="h-5 w-5" />
            </NavLink>
          );
        }

        const GroupIcon = entry.icon;
        const hasActive = entry.items.some((it) => location.pathname.startsWith(it.to));
        const hasPending = entry.items.some((it) => it.to === "/admin/clientes") && pendingClientsCount > 0;
        return (
          <button
            key={entry.id}
            type="button"
            title={entry.label}
            aria-label={entry.label}
            onClick={() => {
              setCollapsed(false);
              setOpenGroups((prev) => ({ ...prev, [entry.id]: true }));
            }}
            className={cn(
              "relative inline-flex h-10 w-10 items-center justify-center rounded-lg transition-colors",
              hasActive
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-secondary/60",
            )}
          >
            <GroupIcon className="h-5 w-5" />
            {hasPending && (
              <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-destructive" />
            )}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="h-screen overflow-hidden bg-background flex">
      <aside
        className={cn(
          "hidden md:flex shrink-0 flex-col border-r border-border bg-surface-elevated sticky top-0 h-screen transition-[width] duration-200",
          collapsed ? "w-16" : "w-64",
        )}
      >
        {collapsed ? (
          <div className="flex flex-col items-center gap-2 px-2 py-4 border-b border-border">
            <Link to={STAFF_HOME_ROUTE} aria-label="Início">
              <Logo showWord={false} to={null} />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => setCollapsed(false)}
              title="Expandir menu"
              aria-label="Expandir menu"
            >
              <PanelLeftOpen className="h-5 w-5" />
            </Button>
          </div>
        ) : (
          <div className="px-5 py-4 border-b border-border">
            <div className="flex items-center justify-between gap-2">
              <Link to={STAFF_HOME_ROUTE} className="flex items-center gap-2">
                <Logo to={null} />
              </Link>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => setCollapsed(true)}
                title="Recolher menu"
                aria-label="Recolher menu"
              >
                <PanelLeftClose className="h-5 w-5" />
              </Button>
            </div>
            <span className="mt-2 inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-primary">
              <ShieldCheck className="h-3 w-3" /> {roleLabel}
            </span>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">{collapsed ? renderCollapsedNav() : renderNav("desktop")}</div>
        <div className={cn("border-t border-border py-3", collapsed ? "px-2" : "px-3")}>
          {!collapsed && (
            <div className="px-2 pb-2 text-xs text-muted-foreground truncate">{account.email}</div>
          )}
          <Button
            variant="ghost"
            size="sm"
            className={cn(collapsed ? "w-full justify-center px-0" : "w-full justify-start")}
            onClick={handleLogout}
            title={collapsed ? "Sair" : undefined}
            aria-label="Sair"
          >
            <LogOut className="h-4 w-4" /> {!collapsed && "Sair"}
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <header className="md:hidden sticky top-0 z-40 border-b border-border bg-surface-elevated/90 backdrop-blur-xl">
          <div className="container flex h-16 items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setMobileOpen(true)} aria-label="Abrir menu">
              <Menu className="h-5 w-5" />
            </Button>
            <Link to={STAFF_HOME_ROUTE} className="flex items-center gap-2">
              <Logo to={null} />
            </Link>
            <span className="ml-auto hidden sm:inline text-sm text-muted-foreground">{account.email}</span>
          </div>
        </header>

        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setMobileOpen(false)}
            />
            <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-surface-elevated border-r border-border flex flex-col">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <Link to={STAFF_HOME_ROUTE} className="flex items-center gap-2">
                  <Logo to={null} />
                </Link>
                <Button variant="ghost" size="sm" onClick={() => setMobileOpen(false)} aria-label="Fechar menu">
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto">{renderNav("mobile")}</div>
            </div>
          </div>
        )}

        {/* Navegação por abas internas: TabBar + host keep-alive renderizando as rotas admin POR ABA.
            `min-h-0` + overflow nos painéis dá rolagem própria por aba (preservada ao alternar). */}
        <main className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <TabBar />
          <TabKeepAliveHost>
            <AdminRoutes />
          </TabKeepAliveHost>
        </main>
      </div>
    </div>
  );
}
